import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { locations } from '@/mocks/locations';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://britfitmobiletyres.co.uk';

const servicesList = [
  { icon: 'ri-steering-2-line', title: 'Mobile Tyre Fitting', desc: 'We come to you — at home, work, or roadside. Fully-equipped vans, wide tyre range.' },
  { icon: 'ri-tools-line', title: 'Puncture Repair', desc: 'Fast, reliable puncture repairs using industry-standard techniques.' },
  { icon: 'ri-compass-3-line', title: 'Wheel Balancing', desc: 'Eliminate vibrations and uneven wear with precision wheel balancing.' },
  { icon: 'ri-sensor-line', title: 'TPMS Services', desc: 'Full TPMS sensor diagnostics, replacement and recalibration.' },
];

export default function LocationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = locations.find((l) => l.slug === slug);

  useEffect(() => {
    if (!location) return;

    document.title = location.metaTitle;
    const desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (desc) desc.content = location.metaDescription;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AutoRepair',
      '@id': `${SITE_URL}/locations/${location.slug}/#business`,
      name: `BritFit Mobile Tyres ${location.city}`,
      description: location.metaDescription,
      url: `${SITE_URL}/locations/${location.slug}`,
      telephone: location.phone,
      email: 'admin@britfitmobiletyres.co.uk',
      priceRange: '££',
      address: {
        '@type': 'PostalAddress',
        streetAddress: location.address,
        addressLocality: location.city,
        postalCode: location.postcode,
        addressCountry: 'GB',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: location.lat,
        longitude: location.lng,
      },
      hasMap: location.googleMapsUrl,
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '00:00',
          closes: '23:59',
        },
      ],
      sameAs: [location.googleMapsUrl],
    };

    const script = document.createElement('script');
    script.id = `ld-location-${location.slug}`;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.getElementById(`ld-location-${location.slug}`)?.remove();
    };
  }, [location]);

  if (!location) return <Navigate to="/locations" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A1D] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2A1D]/70 to-[#0F2A1D]/90" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-[#6EE7B7] text-xs font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/10">
            <i className="ri-map-pin-line" /> {location.region}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Mobile Tyre Fitting<br />
            <span className="text-[#6EE7B7]">{location.city}</span>
          </h1>
          <p className="text-stone-300 text-base md:text-lg max-w-xl mx-auto mb-8">
            Professional mobile tyre fitting, puncture repair and wheel balancing in {location.city} and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${location.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-[#1B4332] hover:bg-[#145228] text-white text-sm font-bold px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line" /> Call {location.phoneFormatted}
            </a>
            <a
              href={location.googleMapsUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-7 py-3 rounded-full border border-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-line" /> View on Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <nav className="flex items-center gap-2 text-xs text-stone-400">
          <Link to="/" className="hover:text-[#1B4332] transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/locations" className="hover:text-[#1B4332] transition-colors cursor-pointer">Locations</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-stone-600 font-medium">{location.city}</span>
        </nav>
      </div>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left — Map + Details */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Google Map */}
          <div className="rounded-xl overflow-hidden border border-stone-100 w-full h-[380px]">
            <iframe
              title={`BritFit Mobile Tyres ${location.city} map`}
              src={location.googleMapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* About this location */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h2 className="text-xl font-bold text-stone-900 mb-3">
              Mobile Tyre Fitting in {location.city}
            </h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-4">
              Our <strong>{location.city}</strong> team covers {location.city} and the wider {location.region} area. Whether you need a new tyre fitted at home, a puncture repaired at work, or emergency roadside assistance, our mobile tyre technicians are ready to come to you — fast.
            </p>
            <p className="text-stone-600 text-sm leading-relaxed">
              We stock a full range of premium and budget tyres from leading brands including Michelin, Pirelli, Continental, Bridgestone, Goodyear and Dunlop. All fitting work is guaranteed and we offer competitive pricing with no hidden charges.
            </p>
          </div>

          {/* Services */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h2 className="text-xl font-bold text-stone-900 mb-5">Services Available in {location.city}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {servicesList.map((s) => (
                <div key={s.title} className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg">
                  <span className="w-9 h-9 flex items-center justify-center bg-[#1B4332]/10 text-[#1B4332] rounded-lg flex-shrink-0">
                    <i className={`${s.icon} text-lg`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-stone-900 mb-0.5">{s.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Contact Card */}
        <div className="flex flex-col gap-5">
          {/* Contact Info */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h2 className="text-base font-bold text-stone-900 mb-5">Contact This Location</h2>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#1B4332]/10 text-[#1B4332] rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 mb-0.5 font-medium uppercase tracking-wide">Address</p>
                  <address className="not-italic text-sm text-stone-700 leading-relaxed">
                    {location.address}<br />
                    {location.city}<br />
                    {location.postcode}
                  </address>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#1B4332]/10 text-[#1B4332] rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 mb-0.5 font-medium uppercase tracking-wide">Phone</p>
                  <a
                    href={`tel:${location.phone}`}
                    className="text-sm text-stone-700 hover:text-[#1B4332] transition-colors cursor-pointer font-semibold"
                  >
                    {location.phoneFormatted}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#1B4332]/10 text-[#1B4332] rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 mb-0.5 font-medium uppercase tracking-wide">Hours</p>
                  <p className="text-sm text-stone-700">7 Days a Week</p>
                  <p className="text-xs text-stone-500">24-hour emergency callouts available</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#1B4332]/10 text-[#1B4332] rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 mb-0.5 font-medium uppercase tracking-wide">Email</p>
                  <a
                    href="mailto:admin@britfitmobiletyres.co.uk"
                    className="text-sm text-stone-700 hover:text-[#1B4332] transition-colors cursor-pointer"
                  >
                    admin@britfitmobiletyres.co.uk
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2.5">
              <a
                href={`tel:${location.phone}`}
                className="flex items-center justify-center gap-2 bg-[#1B4332] hover:bg-[#145228] text-white text-sm font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" /> Call Now
              </a>
              <a
                href={location.googleMapsUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-stone-50 hover:bg-stone-100 text-stone-700 text-sm font-medium py-3 rounded-lg border border-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line" /> Get Directions
              </a>
            </div>
          </div>

          {/* Other Locations */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="text-sm font-bold text-stone-900 mb-4">Other Locations</h3>
            <div className="flex flex-col gap-2">
              {locations
                .filter((l) => l.slug !== slug)
                .map((l) => (
                  <Link
                    key={l.id}
                    to={`/locations/${l.slug}`}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center text-[#1B4332]">
                        <i className="ri-map-pin-line text-sm" />
                      </span>
                      <span className="text-sm text-stone-700 group-hover:text-[#1B4332] transition-colors">{l.city}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-stone-400 group-hover:text-[#1B4332] transition-colors" />
                  </Link>
                ))}
            </div>
            <Link
              to="/locations"
              className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#1B4332] font-semibold hover:underline cursor-pointer"
            >
              View all locations <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
